

import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { dbLabel } from '../../i18n/dbLabels';
import OwlCarousel from 'react-owl-carousel';
import { app } from "@microsoft/teams-js";

import { imageEndPoint } from '../../apis/env';
import { useNavigate } from 'react-router-dom';
import { userBookMarkApi, getBookMarkedBlogsApi, removeBookMarkApi, removeBlogBookMarkApi, blogBookMarkApi } from '../../apis/WebServices';
import { UserContext } from '../../contexts/UserContext';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import { message } from 'antd';
import { blogDeepLink } from '../../apis/env';
import leftImg from "./left.png";
import rightImg from "./right.png";
import nutritionIcon from "../../assets/nutrition-blue.png"; // Fallback image for blogs

// Import shared skeleton system
import { SkeletonBlogCard } from "../skeletons";

const HomeBlogCarousel = ({ data, fetchAllBlogs, navPrevClass, navNextClass }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { userName, setUserName, userToken } = useContext(UserContext)
  const [BookMarkedTrueIds, setAllBookMarkedTrueIds] = useState([]);
  const [bookMarkedStatus, setBookMarkedStatus] = useState(false)



  // FIXED: Fetch bookmarks from dedicated API instead of relying on blog.isBookMarked
  useEffect(() => {
    if (data && userToken) {
      fetchBookmarkedBlogIds();
    }
  }, [data, userToken]);
  
  // 🔧 CRITICAL FIX: Cross-component bookmark synchronization
  useEffect(() => {
    const handleBookmarkSync = (event) => {
      console.log('🔄 HomeBlogCarousel: Received bookmark sync event from', event.detail.component);
      // Only refresh if the event came from a different component
      if (event.detail.component !== 'HomeBlogCarousel') {
        fetchBookmarkedBlogIds();
      }
    };
    
    window.addEventListener('bookmarkChanged', handleBookmarkSync);
    return () => window.removeEventListener('bookmarkChanged', handleBookmarkSync);
  }, []);
  
  // FIXED: Dedicated function to fetch bookmarked blog IDs
  const fetchBookmarkedBlogIds = async () => {
    try {
      if (!userToken || userToken === 'undefined') {
        console.log('⚠️ HomeBlogCarousel: No user token, skipping bookmark fetch');
        return;
      }
      
      const response = await getBookMarkedBlogsApi(userToken);
      console.log('🔍 HomeBlogCarousel: Raw bookmark response:', response);
      
      if (response?.isSuccess) {
        // CRITICAL FIX: Handle backend response format (may be different from video bookmarks)
        let bookmarkData = response.data?.blogs || response.data?.videos || response.data;
        
        // If data is not an array, try to find the array in the response
        if (!Array.isArray(bookmarkData)) {
          console.log('⚠️ HomeBlogCarousel: response.data is not an array, checking for other properties');
          bookmarkData = response.result?.blogs || response.result?.videos || response.items || response.bookmarks || [];
        }
        
        if (Array.isArray(bookmarkData)) {
          const bookmarkedIds = bookmarkData.map(item => item.id || item.blogId).filter(id => id);
          setAllBookMarkedTrueIds(bookmarkedIds);
          console.log('✅ HomeBlogCarousel: Bookmarked IDs fetched from API:', bookmarkedIds);
          console.log('📋 HomeBlogCarousel: State persistence - Loaded', bookmarkedIds.length, 'bookmarked blogs');
        } else {
          console.log('⚠️ HomeBlogCarousel: Could not find array in bookmark response');
          setAllBookMarkedTrueIds([]);
        }
      } else {
        console.log('📋 HomeBlogCarousel: No bookmarked blogs found');
        setAllBookMarkedTrueIds([]);
      }
    } catch (error) {
      console.error('❌ HomeBlogCarousel: Failed to fetch bookmarked IDs:', error);
      setAllBookMarkedTrueIds([]);
    }
  };

  const handleAddBookMark = async (id) => {
    try {
      if (!userToken) {
        message.error(t('common.userNotLoggedIn'));
        return;
      }
      
      const isCurrentlyBookmarked = BookMarkedTrueIds.includes(id);
      
      // CRITICAL FIX: Update UI immediately for instant feedback
      if (isCurrentlyBookmarked) {
        setAllBookMarkedTrueIds(prev => prev.filter(itemId => itemId !== id));
      } else {
        setAllBookMarkedTrueIds(prev => [...prev, id]);
      }
      
      if (isCurrentlyBookmarked) {
        console.log('🗑️ HomeBlogCarousel: Removing bookmark for blog:', id);
        const response = await removeBlogBookMarkApi(id, userToken);

        if (response.isSuccess) {
          message.success(response?.message || 'Blog bookmark removed successfully');
          console.log('✅ HomeBlogCarousel: Blog bookmark removed successfully');
          // Refresh from API after delay to ensure consistency
          setTimeout(() => fetchBookmarkedBlogIds(), 500);
          // 🔧 CRITICAL FIX: Notify other components
          window.dispatchEvent(new CustomEvent('bookmarkChanged', { 
            detail: { blogId: id, action: 'remove', component: 'HomeBlogCarousel' } 
          }));
        } else {
          // REVERT UI change if API failed
          setAllBookMarkedTrueIds(prev => [...prev, id]);
          message.error(response?.message || 'Failed to remove blog bookmark');
        }
      } else {
        console.log('➕ HomeBlogCarousel: Adding bookmark for blog:', id);
        const response = await blogBookMarkApi(id, userToken);

        if (response.isSuccess) {
          message.success(response?.message || 'Blog bookmark added successfully');
          console.log('✅ HomeBlogCarousel: Blog bookmark added successfully');
          // Refresh from API after delay to ensure consistency
          setTimeout(() => fetchBookmarkedBlogIds(), 500);
          // 🔧 CRITICAL FIX: Notify other components
          window.dispatchEvent(new CustomEvent('bookmarkChanged', { 
            detail: { blogId: id, action: 'add', component: 'HomeBlogCarousel' } 
          }));
        } else {
          // REVERT UI change if API failed
          setAllBookMarkedTrueIds(prev => prev.filter(itemId => itemId !== id));
          message.error(response?.message || 'Failed to add blog bookmark');
        }
      }
    } catch (error) {
      console.error('❌ HomeBlogCarousel: Bookmark operation failed:', error);
      // REVERT UI change if error occurred
      const isCurrentlyBookmarked = BookMarkedTrueIds.includes(id);
      if (isCurrentlyBookmarked) {
        setAllBookMarkedTrueIds(prev => [...prev, id]);
      } else {
        setAllBookMarkedTrueIds(prev => prev.filter(itemId => itemId !== id));
      }
      message.error(error?.message || 'Blog bookmark operation failed');
    }
  };

  const handleBlogNavigate = () => {
    navigate("/blog-detail", { state: { data: "" } });
  }

 
  const handleDeleteBookMark = async (id) => {
    try {
      const response = await removeBookMarkApi(id, userToken);
      if (response?.isSuccess) {
        fetchVideos();
     
        message.success(response?.message)
      }
      else {
        message.error(response?.message)
      }
    } catch (error) {

    } finally {

    }
  }


  const handleNavigate = (data) => {
    const blogId = data?.id;

    if (!blogId) {
      console.error('Missing blog ID:', blogId);
      return;
    }

    console.log('📄 BLOG CLICK: Home -> Blog Detail (direct navigation)');
    console.log('📍 Navigation: Home tab -> Blog detail view');
    
    // Direct navigation to blog detail like it was working before
    navigate(`/blog-detail?blogId=${blogId}&from=home`);
  };

  // Show skeleton loading if data is not available or empty
  if (!data || data.length === 0) {
    return (
      <div className="row">
        {/* Show 3 blog skeleton cards */}
        {[...Array(3)].map((_, index) => (
          <SkeletonBlogCard key={index} />
        ))}
      </div>
    );
  }

  return (
    <OwlCarousel
      key={`blog-carousel-working-${data?.length || 0}`}
      dots={false}
      autoplay={true}
      autoplayTimeout={3000}
      rewind={true}
      rtl={false}
      loop={data?.length > 3}
      responsiveClass={true}
      smartSpeed={800}
      nav={!navPrevClass && !navNextClass}
      margin={15}
      mouseDrag={true}
      touchDrag={true}
      responsive={{
        0: { items: 1 },
        600: { items: 2 },
        1024: { items: 3 },
        1366: { items: 3 },
      }}
      navText={[
        `<img src="${leftImg}" alt="Previous" style="width: 30px; height: 30px; cursor: pointer;" />`,
        `<img src="${rightImg}" alt="Next" style="width: 30px; height: 30px; cursor: pointer;" />`
      ]}
      ref={(carousel) => {
        if (carousel && navPrevClass && navNextClass) {
          // Use setTimeout to ensure DOM elements are available
          setTimeout(() => {
            const prevBtn = document.querySelector(`.${navPrevClass}`);
            const nextBtn = document.querySelector(`.${navNextClass}`);
            if (prevBtn && nextBtn) {
              prevBtn.onclick = () => {
                try {
                  carousel.prev();
                } catch (error) {
                  console.warn('Blog carousel prev navigation failed:', error);
                }
              };
              nextBtn.onclick = () => {
                try {
                  carousel.next();
                } catch (error) {
                  console.warn('Blog carousel next navigation failed:', error);
                }
              };
              console.log(`✅ Blog navigation buttons connected for ${navPrevClass}/${navNextClass}`);
            } else {
              console.warn(`⚠️ Blog navigation buttons not found: ${navPrevClass}/${navNextClass}`);
            }
          }, 100);
        }
      }}
    >
      {data?.map((key, index) => (
        <div className="swiper-slide" key={index}>
          <div className="item">
            <div className="card explore-box card-animate" style={{ minHeight: '350px', display: 'flex', flexDirection: 'column' }}>
              <div className="explore-place-bid-img" onClick={() => handleNavigate(key)}>
                <img
                  src={key?.thumbnail || nutritionIcon}
                  alt={key?.title || key?.blogName}
                  className="img-fluid card-img-top explore-img"
                  style={{ height: "200px", objectFit: "cover" }}
                />
                <div className="bg-overlay"></div>
              </div>
              <div className="card-body" style={{ flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="card-title mb-0 flex-grow-1" 
                      style={{ 
                        cursor: "pointer", 
                        fontSize: "14px",
                        lineHeight: "1.2",
                        height: "17px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                      }} 
                      onClick={() => handleNavigate(key)}>
                    <a className="text-primary">
                      {key?.title || key?.blogName}
                    </a>
                  </h6>
                  <button
                    className="btn btn-sm p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      !bookMarkedStatus && handleAddBookMark(key?.id);
                    }}
                    disabled={bookMarkedStatus}
                    style={{ background: "none", border: "none", cursor: "pointer" }}
                  >
                    <i className={`mdi ${BookMarkedTrueIds.includes(key?.id) ? "mdi-bookmark" : "mdi-bookmark-outline"} fs-18`} 
                       style={{ 
                           color: BookMarkedTrueIds.includes(key?.id) ? '#16f69f' : '#6c757d',
                           fontSize: '18px'
                       }}></i>
                  </button>
                </div>
                <div>
                  <p className="text-muted mb-1">
                    <small>{dbLabel(key?.category?.name || "LIFESTYLE", i18n.language)}</small>
                  </p>
                  <p className="text-muted mb-0" style={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  }}>
                    <small>{key?.description || "Understanding the profound effects..."}</small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))} 
    </OwlCarousel>
  );
};

const styles = {
  slideItem: {
    padding: '0 10px', 
    position: 'relative', 
  },
  imgFluid: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
};

export default HomeBlogCarousel;
